import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.info.crop.' + key, params);
const lib = (key, params) => app.translator.trans('ppk2-profile.lib.photo.' + key, params);

/** Frame aspect 4:3; result at most 1600×1200 (the server caps 1600 px and re-encodes anyway). */
export const ASPECT = 4 / 3;
export const OUT_MAX_W = 1600;
export const OUT_MIN_W = 200;
const VIEW_W = 480;
const VIEW_H = VIEW_W / ASPECT;
const ZOOM_MAX = 4; // × the "cover" scale

/**
 * Inline crop / scale panel: drag to move, slider to zoom, fixed 4:3 frame.
 * attrs: file (File), onsave(blob), oncancel(), uploading
 */
export default class PhotoCropper extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.img = null;
    this.zoom = 1; // 1 .. ZOOM_MAX
    this.cx = 0; // image point (source px) shown in the frame centre
    this.cy = 0;
    this.error = null;
    this.drag = null;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.canvas = vnode.dom.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.url = URL.createObjectURL(this.attrs.file);
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth < OUT_MIN_W || img.naturalHeight < OUT_MIN_W / ASPECT) {
        this.error = lib('too_small', {
          w: OUT_MIN_W,
          h: OUT_MIN_W / ASPECT
        });
      }
      this.img = img;
      this.cx = img.naturalWidth / 2;
      this.cy = img.naturalHeight / 2;
      this.draw();
      m.redraw();
    };
    img.onerror = () => {
      this.error = lib('bad_type');
      m.redraw();
    };
    img.src = this.url;
  }
  onremove() {
    if (this.url) URL.revokeObjectURL(this.url);
  }

  /** px of the viewport per source px */
  scale() {
    const img = this.img;
    return Math.max(VIEW_W / img.naturalWidth, VIEW_H / img.naturalHeight) * this.zoom;
  }

  /** Source rectangle visible in the frame. */
  rect() {
    const k = this.scale();
    const w = VIEW_W / k;
    const h = VIEW_H / k;
    const img = this.img;
    this.cx = Math.min(Math.max(this.cx, w / 2), img.naturalWidth - w / 2);
    this.cy = Math.min(Math.max(this.cy, h / 2), img.naturalHeight - h / 2);
    return {
      x: this.cx - w / 2,
      y: this.cy - h / 2,
      w,
      h
    };
  }
  draw() {
    if (!this.img || !this.ctx) return;
    const r = this.rect();
    const ctx = this.ctx;
    ctx.fillStyle = '#ffffff'; // PNG transparency -> white, as on the server
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.img, r.x, r.y, r.w, r.h, 0, 0, VIEW_W, VIEW_H);
  }
  pointerdown(e) {
    if (!this.img) return;
    e.preventDefault();
    this.canvas.setPointerCapture && this.canvas.setPointerCapture(e.pointerId);
    this.drag = {
      x: e.clientX,
      y: e.clientY,
      cx: this.cx,
      cy: this.cy
    };
  }
  pointermove(e) {
    e.redraw = false; // the canvas is painted directly
    if (!this.drag) return;
    // On-screen size may differ from VIEW_W (responsive canvas).
    const ratio = VIEW_W / this.canvas.getBoundingClientRect().width;
    const k = this.scale();
    this.cx = this.drag.cx - (e.clientX - this.drag.x) * ratio / k;
    this.cy = this.drag.cy - (e.clientY - this.drag.y) * ratio / k;
    this.draw();
  }
  pointerup() {
    this.drag = null;
  }
  setZoom(z) {
    this.zoom = Math.min(Math.max(z, 1), ZOOM_MAX);
    this.draw();
  }
  wheel(e) {
    if (!this.img) return;
    e.preventDefault();
    this.setZoom(this.zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
    m.redraw();
  }
  save() {
    const r = this.rect();
    const w = Math.round(Math.min(OUT_MAX_W, Math.max(OUT_MIN_W, r.w)));
    const h = Math.round(w / ASPECT);
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.img, r.x, r.y, r.w, r.h, 0, 0, w, h);
    const tryQuality = q => out.toBlob(blob => {
      if (!blob) return this.error = lib('failed');
      if (blob.size > 2 * 1024 * 1024 && q > 0.5) return tryQuality(q - 0.15);
      this.attrs.onsave(blob);
    }, 'image/jpeg', q);
    tryQuality(0.9);
  }
  view() {
    const busy = this.attrs.uploading;
    return m("div", {
      className: "Ppk2Crop",
      role: "dialog",
      "aria-label": t('title')
    }, m("div", {
      className: "Ppk2Crop-title"
    }, m(Icon, {
      name: "fas fa-crop-simple"
    }), " ", t('title')), m("div", {
      className: "Ppk2Crop-hint"
    }, t('hint')), m("div", {
      className: "Ppk2Crop-frame"
    }, m("canvas", {
      width: VIEW_W,
      height: VIEW_H,
      className: this.drag ? 'dragging' : '',
      onpointerdown: e => this.pointerdown(e),
      onpointermove: e => this.pointermove(e),
      onpointerup: () => this.pointerup(),
      onpointercancel: () => this.pointerup(),
      onwheel: e => this.wheel(e)
    })), this.error ? m("div", {
      className: "Ppk2NickStatus Ppk2NickStatus--invalid"
    }, this.error) : null, m("div", {
      className: "Ppk2Crop-zoom"
    }, m(Icon, {
      name: "fas fa-magnifying-glass-minus"
    }), m("input", {
      type: "range",
      name: "ppk2CropZoom",
      min: "1",
      max: ZOOM_MAX,
      step: "0.01",
      value: this.zoom,
      "aria-label": t('zoom'),
      title: t('zoom'),
      disabled: !this.img || busy,
      oninput: e => this.setZoom(parseFloat(e.target.value))
    }), m(Icon, {
      name: "fas fa-magnifying-glass-plus"
    })), m("div", {
      className: "Ppk2Crop-buttons"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-check",
      loading: busy,
      disabled: !this.img || !!this.error || busy,
      onclick: () => this.save()
    }, t('save')), m(Button, {
      className: "Button",
      disabled: busy,
      onclick: () => this.attrs.oncancel()
    }, t('cancel'))));
  }
}
flarum.reg.add('ppk2-profile', 'forum/components/PhotoCropper', PhotoCropper);